import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Search, Loader2, CheckCircle2, XCircle, Ban, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Availability Inspector — pick a date + location, and see exactly what is
 * blocking (or freeing) each hour. Answers the question "why isn't this hour
 * on the public booking page?" without needing DB access.
 */
export default function AvailabilityInspector() {
  const [locations, setLocations] = useState([]);
  const [locSlug, setLocSlug] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/locations").then((r) => {
      setLocations(r.data);
      if (r.data[0]) setLocSlug(r.data[0].slug);
    });
  }, []);

  const inspect = async () => {
    if (!locSlug || !date) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/availability/inspect?location_slug=${locSlug}&date=${date}`);
      setData(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (locSlug && date) inspect(); /* eslint-disable-next-line */ }, [locSlug, date]);

  const inputCls = "w-full rounded-xl border border-sand/70 bg-cream px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-navy";

  return (
    <div data-testid="availability-inspector" className="bg-cream-soft border border-sand/70 rounded-3xl p-6 md:p-8 mb-6">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-1 flex items-center gap-2">
            <Search size={12} /> Availability inspector
          </div>
          <h3 className="font-serif text-2xl text-ink">Why is this slot blocked?</h3>
          <p className="text-xs text-ink-soft mt-1 max-w-md">
            Pick a date and a location — see the exact reason each hour is available, closed, or blocked (session / block / auto buffer).
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-5 max-w-2xl">
        <select
          value={locSlug}
          onChange={(e) => setLocSlug(e.target.value)}
          data-testid="inspector-location"
          className={inputCls}
        >
          {locations.map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
        </select>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="inspector-date"
          className={inputCls}
        />
      </div>

      {loading && (
        <div className="py-10 flex items-center justify-center text-ink-muted"><Loader2 size={16} className="animate-spin mr-2" /> Inspecting…</div>
      )}

      {data && !loading && (
        <div data-testid="inspector-results">
          <div className="mb-3 text-sm text-ink-soft">
            {data.weekday_name} · {format(new Date(data.date), "d MMM yyyy")} · {data.location}
            {data.hours_open ? (
              <span className="ml-2 text-ink">— open {formatHour(data.hours_open[0])}–{formatHour(data.hours_open[1])}</span>
            ) : (
              <span className="ml-2 text-clay-deep">— closed all day (no schedule set for this weekday)</span>
            )}
          </div>
          <div className="rounded-2xl border border-sand/60 bg-cream overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-sand/30 text-ink-soft text-[11px] uppercase tracking-[0.16em]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium w-24">Hour</th>
                  <th className="text-left px-4 py-2.5 font-medium w-32">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.hours.map((h) => {
                  const style = statusStyles[h.status];
                  const Icon = style.icon;
                  return (
                    <tr key={h.hour} data-testid={`inspector-row-${h.hour}-${h.status}`} className={`border-t border-sand/40 ${style.rowBg}`}>
                      <td className="px-4 py-2.5 tabular-nums text-ink font-medium">{formatHourStr(h.hour)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${style.pill}`}>
                          <Icon size={11} /> {style.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-ink-muted">{h.reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function formatHour(h) {
  if (h === 0) return "12am";
  if (h < 12) return `${h}am`;
  if (h === 12) return "12pm";
  return `${h - 12}pm`;
}

function formatHourStr(hhmm) {
  const h = parseInt(hhmm.split(":")[0], 10);
  return formatHour(h);
}

const statusStyles = {
  available: { label: "Available", icon: CheckCircle2, pill: "bg-sage/15 text-sage-deep border border-sage/40", rowBg: "" },
  blocked:   { label: "Blocked",   icon: Ban,           pill: "bg-clay/15 text-clay-deep border border-clay/40", rowBg: "bg-clay/5" },
  closed:    { label: "Closed",    icon: XCircle,       pill: "bg-ink/8 text-ink-muted border border-sand",       rowBg: "bg-sand/10" },
  past:      { label: "Past",      icon: Clock,         pill: "bg-navy/8 text-navy border border-navy/25",         rowBg: "" },
};
